import { zonedTimeToUtc } from 'date-fns-tz';

export const MAX_SHIFTS_PER_BOOKING = 100;
export const MAX_STORE_RESULTS = 12;
const RECENT_STORES_KEY = 'adminAssignShifts.recentStoreIds';
const MAX_RECENT_STORES = 6;
const MINUTES_PER_HOUR = 60;
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export const DEFAULT_SETTINGS = {
  start: '09:00',
  end: '17:00',
  unpaidBreakMinutes: '30',
  rate: '',
  negotiable: false,
  billToMedicare: false,
};

const pad = (value) => String(value).padStart(2, '0');

/** Formats a Date picked in the calendar as its local calendar day, YYYY-MM-DD. */
export const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Parses YYYY-MM-DD into a local Date at midnight (what the date picker works with). */
export const fromDateString = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatDateLabel = (dateString) => fromDateString(dateString).toLocaleDateString('en-AU', {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
});

export const formatMoney = (cents) => `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

const getPaidHours = (row) => {
  const shiftMinutes = toMinutes(row.end) - toMinutes(row.start);
  return (shiftMinutes - Number(row.unpaidBreakMinutes || 0)) / MINUTES_PER_HOUR;
};

/** Same formula as the Add Shifts page: whole dollars, in cents. */
export const getRowTotalCents = (row) => Math.round(getPaidHours(row) * Number(row.rate || 0)) * 100;

/** Returns the problems with a row, empty when it can be sent to the server. */
export const getRowProblems = (row) => {
  const problems = [];
  if (!TIME_REGEX.test(row.start) || !TIME_REGEX.test(row.end)) {
    problems.push('Set a start and end time.');
  } else if (row.end <= row.start) {
    problems.push('End must be after start.');
  }
  const breakMinutes = Number(row.unpaidBreakMinutes);
  if (row.unpaidBreakMinutes === '' || !Number.isInteger(breakMinutes) || breakMinutes < 0) {
    problems.push('Break must be whole minutes.');
  } else if (TIME_REGEX.test(row.start) && TIME_REGEX.test(row.end) && breakMinutes >= toMinutes(row.end) - toMinutes(row.start)) {
    problems.push('Break must be shorter than the shift.');
  }
  const rate = Number(row.rate);
  if (row.rate === '' || !Number.isFinite(rate) || rate < 0 || (rate === 0 && !row.billToMedicare)) {
    problems.push('Enter an hourly rate.');
  }
  return problems;
};

/** Soft warning for a row that is valid but looks like a typo. Does not block booking. */
export const getRowWarning = (row) => {
  const rate = Number(row.rate);
  if (!row.billToMedicare && rate > 0 && (rate < 20 || rate > 200)) {
    return `Check the rate: $${rate}/h is unusual.`;
  }
  return '';
};

export const compareRows = (a, b) => (
  a.date.localeCompare(b.date) || a.start.localeCompare(b.start) || a.storeName.localeCompare(b.storeName)
);

const getRowKey = (row) => `${row.storeId}|${row.date}|${row.start}|${row.end}`;

let rowCounter = 0;

/**
 * Creates one row per picked date (YYYY-MM-DD) for the store, skipping any that already exist in `existingRows`.
 * @returns {{ rows: Array<Object>, skipped: number }}
 */
export const buildRows = ({ store, dates, settings, existingRows }) => {
  const existingKeys = new Set(existingRows.map(getRowKey));
  const rows = [];
  let skipped = 0;
  dates.forEach(date => {
    const row = {
      id: `row-${rowCounter += 1}`,
      storeId: store._id,
      storeName: store.name,
      timeZone: store.timeZone,
      date,
      start: settings.start,
      end: settings.end,
      unpaidBreakMinutes: String(settings.unpaidBreakMinutes),
      rate: String(settings.rate),
      negotiable: settings.negotiable,
      billToMedicare: settings.billToMedicare,
    };
    if (existingKeys.has(getRowKey(row))) {
      skipped += 1;
      return;
    }
    existingKeys.add(getRowKey(row));
    rows.push(row);
  });
  return { rows, skipped };
};

/** Whether the shift has ended, is in progress or is upcoming, judged in the store's time zone. Null when unknown. */
export const getRowPhase = (row, now = Date.now()) => {
  if (!row.timeZone || !TIME_REGEX.test(row.start) || !TIME_REGEX.test(row.end)) {
    return null;
  }
  try {
    const startTime = zonedTimeToUtc(`${row.date}T${row.start}:00`, row.timeZone).getTime();
    const endTime = zonedTimeToUtc(`${row.date}T${row.end}:00`, row.timeZone).getTime();
    if (endTime <= now) {
      return 'ENDED';
    }
    return startTime <= now ? 'IN_PROGRESS' : 'UPCOMING';
  } catch (error) {
    return null;
  }
};

export const buildPayload = ({ locum, rows, options }) => ({
  locumId: locum._id,
  shifts: rows.map(row => ({
    storeId: row.storeId,
    date: row.date,
    start: row.start,
    end: row.end,
    rate: Number(row.rate),
    unpaidBreakMinutes: Number(row.unpaidBreakMinutes),
    negotiable: row.negotiable,
    billToMedicare: row.billToMedicare,
  })),
  options,
});

/** Replaces `shifts[0]` labels in server errors with the row number shown in the table. */
export const formatServerErrors = (errors) => (
  (errors || []).map(message => message.replace(/shifts\[(\d+)\]/g, (match, index) => `Row ${Number(index) + 1}`))
);

const getStoreSearchText = (store) => {
  const address = store.address || {};
  const organization = store.organization || {};
  return [
    store.name, address.suburb, address.city, address.state, address.postcode, organization.registeredBusinessName,
  ].filter(Boolean).join(' ').toLowerCase();
};

/** Adds a lowercase search string to each store once, so searching 600 stores per keystroke stays instant. */
export const indexStores = (stores) => (
  (stores || []).map(store => ({ store, text: getStoreSearchText(store), name: (store.name || '').toLowerCase() }))
);

/** Every word must appear somewhere in the store's name, address or registered business name. Name prefix matches rank first. */
export const searchStores = (indexedStores, query, limit = MAX_STORE_RESULTS) => {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return [];
  }
  return indexedStores
    .filter(({ text }) => tokens.every(token => text.includes(token)))
    .map(entry => ({ ...entry, rank: entry.name.startsWith(tokens[0]) ? 0 : 1 }))
    .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map(({ store }) => store);
};

const DEFAULT_COMMISSION_RATE = 0.05;

export const PII_STATUS_LABELS = {
  NOT_PROVIDED: 'Not provided',
  DECLARED_NONE: 'Declared no PII',
  SELF_DECLARED: 'Self-declared (has PII)',
  VERIFIED: 'Verified',
};

export const formatPercent = (rate) => `${Math.round(rate * 10000) / 100}%`;

const joinParts = (parts, separator) => parts.filter(part => typeof part === 'string' && part.trim().length > 0).join(separator);

/**
 * Mirrors the server's store summary (getStoreAdminSummary) from a store in the admin's profile, so the store
 * card can show before any booking is previewed. Bank details are reduced to present or not.
 */
export const toStoreSummary = (store) => {
  const address = store.address || {};
  const organization = store.organization || {};
  return {
    _id: store._id,
    name: store.name,
    type: store.type || null,
    registeredBusinessName: organization.registeredBusinessName || null,
    tradingAs: organization.tradingAs || null,
    abn: organization.abn || null,
    acn: organization.acn || null,
    address: joinParts([
      joinParts([address.mall, address.shopNumber, address.street], ' '),
      joinParts([address.suburb, address.state, address.postcode], ' '),
    ], ', '),
    timeZone: address.timeZone || null,
    commissionRate: typeof store.commissionRate === 'number' ? store.commissionRate : DEFAULT_COMMISSION_RATE,
    hasBankDetails: !!(store.banking && store.banking.bsb && store.banking.accountNumber),
    hasOnboardingDocument: !!store.locumOnboardingDocument,
    contactEmailAddress: store.contact && store.contact.emailAddress ? store.contact.emailAddress : null,
    providerNumber: undefined,
    linked: true,
  };
};

export const formatStoreAddress = (store) => {
  const address = store.address || {};
  return [address.suburb || address.city, address.state, address.postcode].filter(Boolean).join(' ');
};

export const getRecentStoreIds = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_STORES_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const rememberStore = (storeId) => {
  try {
    const recent = [storeId, ...getRecentStoreIds().filter(id => id !== storeId)].slice(0, MAX_RECENT_STORES);
    window.localStorage.setItem(RECENT_STORES_KEY, JSON.stringify(recent));
  } catch (error) {
    // Recent stores are a convenience only, ignore storage failures (private mode, quota).
  }
};

export const formatDurationHours = (row) => {
  const hours = getPaidHours(row);
  return Number.isFinite(hours) && hours > 0 ? `${Math.round(hours * 100) / 100}h paid` : '';
};

/** Lists YYYY-MM-DD dates from `from` to `to` inclusive, optionally leaving out Saturdays and Sundays. */
export const getDatesInRange = (from, to, skipWeekends) => {
  const dates = [];
  const cursor = fromDateString(from);
  const last = fromDateString(to);
  while (cursor <= last && dates.length <= MAX_SHIFTS_PER_BOOKING) {
    const day = cursor.getDay();
    if (!skipWeekends || (day !== 0 && day !== 6)) {
      dates.push(toDateString(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};
